/**
 * Row actions for the applications table (Sprint 365 Stream B; CHG-GW-001).
 *
 * The menu is keyboard accessible (Radix DropdownMenu) and carries a per-row
 * `aria-label` so screen readers can tell which application each menu belongs
 * to. Mutations are gated by `Module:ai_gateway#admin` upstream; this component
 * only renders actions when the caller allows it.
 */

import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useI18n } from '@/i18n';
import type { GatewayApplication } from '../types';

export interface ApplicationRowActionsProps {
  application: GatewayApplication;
  canAdmin: boolean;
  onEdit: (application: GatewayApplication) => void;
  onArchive: (application: GatewayApplication) => void;
}

export function ApplicationRowActions({
  application,
  canAdmin,
  onEdit,
  onArchive,
}: ApplicationRowActionsProps) {
  const { t } = useI18n();

  if (!canAdmin) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('gateway.applications.actionsFor', { name: application.name })}
          data-testid={`application-actions-${application.id}`}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onSelect={() => onEdit(application)}
          data-testid={`application-edit-${application.id}`}
        >
          <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('gateway.applications.edit')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => onArchive(application)}
          data-testid={`application-archive-${application.id}`}
          className="text-red-600 focus:text-red-600"
        >
          <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('gateway.applications.archive')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
